import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useNavigate } from "react-router-dom";
import SharedHeader from "../common/SharedHeader";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosInstance.js";
import io from "socket.io-client";
import PageWrapper from "../components/PageWrapper.jsx";
import { LanguageContext } from "../contexts/LanguageContext.js";
import { ToastContainer, toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";

const SOCKET_SERVER_URL =
  process.env.REACT_APP_SOCKET_URL || "https://adey.lucky.et";

const StakeSelectionPage = ({
  setCurrentPage = () => {},
  isSidebarOpen = false,
  setIsSidebarOpen = () => {},
}) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  const translations = {
    en: {
      title: "Select Stake",
      loading: "Loading...",
      noGames: "No games available",
      currency: "ETB",
      join: "JOIN",
      open: "OPEN",
      view: "VIEW",
      create: "CREATE",
      closed: "CLOSED",
      live: "LIVE",
      ready: "READY",
      completed: "ENDED",
      starting: "STARTING",
      gameAlreadyActive: "A game is already active for this stake",
      errorGeneric: "Something went wrong. Please try again.",
      winChance: "chance",
      stake: "Stake",
      status: "Status",
      players: "Players",
      prize: "Prize",
      action: "Action",
    },
    am: {
      title: "ውርርድ ይምረጡ",
      loading: "በመጫን ላይ...",
      noGames: "ምንም ጨዋታ የለም",
      currency: "ብር",
      join: "ተቀላቀል",
      open: "ክፈት",
      view: "እይ",
      create: "ፍጠር",
      closed: "ዝግ",
      live: "በቀጥታ",
      ready: "ዝግጁ",
      completed: "አልቋል",
      starting: "በመጀመር ላይ",
      gameAlreadyActive: "ጨዋታ በሂደት ላይ ነው",
      errorGeneric: "ስህተት ተከስቷል።",
      winChance: "የማሸነፍ እድል",
      stake: "ውርርድ",
      status: "ሁኔታ",
      players: "ተጫዋቾች",
      prize: "ሽልማት",
      action: "ድርጊት",
    },
    or: {
      title: "Hirmaannaa Filadhu",
      loading: "Fe'amaa jira...",
      noGames: "Taphoonni hin jiran",
      currency: "ETB",
      join: "SEENI",
      open: "BANI",
      view: "ILAALI",
      create: "UUMI",
      closed: "CUFAME",
      live: "LIVE",
      ready: "QOPHAA'E",
      completed: "XUMURAME",
      starting: "JALQABAA JIRA",
      gameAlreadyActive: "Taphi hojii irra jira",
      errorGeneric: "Dogoggorri uumame",
      winChance: "carraa",
      stake: "Hirmaannaa",
      status: "Haala",
      players: "Taphataa",
      prize: "Badhaasa",
      action: "Gochaa",
    },
    ti: {
      title: "ምረጽ ውርርድ",
      loading: "ይጽዓን ኣሎ...",
      noGames: "ጸወታ የለን",
      currency: "ብር",
      join: "ተጸንበር",
      open: "ክፈት",
      view: "ርአ",
      create: "ፍጠር",
      closed: "ዕጹው",
      live: "LIVE",
      ready: "ድሉው",
      completed: "ተወዲኡ",
      starting: "ይጅምር ኣሎ",
      gameAlreadyActive: "ጸወታ ኣብ ስራሕ ኣሎ",
      errorGeneric: "ጌጋ ተፈጢሩ",
      winChance: "ዕድል",
      stake: "ውርርድ",
      status: "ኩነታት",
      players: "ተጻወቲ",
      prize: "ዓስቢ",
      action: "ስራሕ",
    },
    wo: {
      title: "Hirmaannaa Filadhu",
      loading: "Fe'amaa jira...",
      noGames: "Taphoonni hin jiran",
      currency: "ETB",
      join: "SEENI",
      open: "BANI",
      view: "ILAALI",
      create: "UUMI",
      closed: "CUFAME",
      live: "LIVE",
      ready: "QOPHAA'E",
      completed: "XUMURAME",
      starting: "JALQABAA JIRA",
      gameAlreadyActive: "Taphi hojii irra jira",
      errorGeneric: "Dogoggorri uumame",
      winChance: "carraa",
      stake: "Hirmaannaa",
      status: "Haala",
      players: "Taphataa",
      prize: "Badhaasa",
      action: "Gochaa",
    },
  };

  const t = translations[language] || translations.en;

  const showToast = useCallback((message, type = "error") => {
    toast[type](message, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      toastId: message,
    });
  }, []);

  const fetchInitialGames = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/game/stakes");
      if (response.data.success) {
        setGames(response.data.games || []);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    setCurrentPage(t.title);
    fetchInitialGames();

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(SOCKET_SERVER_URL, {
      path: "/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
      secure: true,
    });

    socketRef.current = socket;

    socket.on("globalStakesUpdate", (data) => {
      if (data && Array.isArray(data.games)) {
        setGames((prevGames) => {
          const prevGamesMap = new Map(prevGames.map((g) => [g.stake_id, g]));
          return data.games.map((incomingGame) => ({
            ...incomingGame,
            user_has_cartellas:
              prevGamesMap.get(incomingGame.stake_id)?.user_has_cartellas || false,
          }));
        });
      }
    });

    socket.on("connect_error", () => {
      fetchInitialGames();
    });

    socket.on("disconnect", () => {
      fetchInitialGames();
    });

    socket.on("error", (message) => {
      if (typeof message === "string" && message.includes("Authentication failed")) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        navigate("/login");
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [fetchInitialGames, navigate, setCurrentPage, t.title]);

  const handleAction = async (gameData) => {
    try {
      setLoading(true);

      if (
        gameData.user_has_cartellas &&
        (gameData.status === "pending" || gameData.status === "active")
      ) {
        navigate("/bingo-game", {
          state: {
            gameId: gameData.id,
            stakeId: gameData.stake_id,
            gameStatus: gameData.status,
            nextDrawTime: gameData.next_draw_time,
            stakeAmount: gameData.stake_amount,
            isWeeklyPaying: gameData.is_weekly_paying,
          },
        });
        return;
      }

      if (gameData.status === "pending") {
        const response = await axiosInstance.post(`/game/join/${gameData.id}`, {});
        if (response.data.success) {
          navigate("/bingo-game", {
            state: {
              gameId: response.data.gameId,
              stakeId: gameData.stake_id,
              cartellaIds: response.data.cartellaIds,
              gameStatus: response.data.gameStatus,
              nextDrawTime: response.data.nextDrawTime,
              stakeAmount: response.data.stake_amount,
              isWeeklyPaying: gameData.is_weekly_paying,
            },
          });
        }
      }

      if (gameData.status === "unavailable") {
        const response = await axiosInstance.post(`/game/create`, {
          stakeId: gameData.stake_id,
        });
        if (response.data.success) {
          navigate("/bingo-game", {
            state: {
              gameId: response.data.gameId,
              stakeId: gameData.stake_id,
              cartellaIds: response.data.cartellaIds,
              gameStatus: response.data.gameStatus,
              nextDrawTime: response.data.nextDrawTime,
              stakeAmount: response.data.stake_amount,
              isWeeklyPaying: gameData.is_weekly_paying,
            },
          });
        }
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        navigate("/login");
      } else if (err.response?.status === 409) {
        showToast(t.gameAlreadyActive, "warning");
      } else {
        showToast(t.errorGeneric, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (gameData, timeToStart, t) => {
    if (
      gameData.user_has_cartellas &&
      (gameData.status === "pending" || gameData.status === "active")
    ) {
      return {
        text: gameData.status === "active" ? t.live : (timeToStart || t.ready),
        color: gameData.status === "active" ? "text-green-400" : "text-cyan-400",
      };
    } else if (gameData.status === "pending") {
      if (timeToStart === (gameData.is_weekly_paying ? "00:00:00:00" : "00:00")) {
        return { text: t.starting, color: "text-red-400" };
      }
      return { text: timeToStart, color: "text-orange-400" };
    } else if (gameData.status === "active") {
      return { text: t.live, color: "text-red-500 animate-pulse" };
    } else if (gameData.status === "unavailable") {
      return { text: t.ready, color: "text-emerald-400" };
    }
    return { text: t.completed, color: "text-gray-500" };
  };

  const getButtonDisplay = (gameData, timeToStart, t) => {
    if (
      gameData.user_has_cartellas &&
      (gameData.status === "pending" || gameData.status === "active")
    ) {
      return { text: t.open, disabled: false, variant: "primary" };
    } else if (gameData.status === "pending") {
      if (timeToStart === (gameData.is_weekly_paying ? "00:00:00:00" : "00:00")) {
        return { text: t.closed, disabled: true, variant: "disabled" };
      }
      return { text: t.join, disabled: false, variant: "success" };
    } else if (gameData.status === "active") {
      return { text: t.view, disabled: true, variant: "secondary" };
    } else if (gameData.status === "unavailable") {
      return { text: t.create, disabled: false, variant: "primary" };
    }
    return { text: t.closed, disabled: true, variant: "disabled" };
  };

  const getButtonStyle = (variant) => {
    const styles = {
      primary: "bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-500/25",
      success: "bg-green-600 hover:bg-green-700 text-white shadow-md shadow-green-500/25",
      secondary: "bg-gray-600 hover:bg-gray-700 text-white",
      disabled: "bg-gray-700 text-gray-400 cursor-not-allowed",
    };
    return styles[variant] || styles.disabled;
  };

  const sortedGames = useMemo(() => {
    return [...games].sort((a, b) => {
      const stakeA = parseFloat(a.stake_amount);
      const stakeB = parseFloat(b.stake_amount);
      return stakeA - stakeB;
    });
  }, [games]);

  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const getTimeRemaining = useCallback((nextDrawTime, isWeeklyPaying) => {
    if (!nextDrawTime) return null;
    const drawTime = new Date(nextDrawTime).getTime();
    const timeLeftMs = drawTime - currentTime;
    if (timeLeftMs <= 0) {
      return isWeeklyPaying ? "00:00:00:00" : "00:00";
    }
    const totalSeconds = Math.floor(timeLeftMs / 1000);
    if (isWeeklyPaying) {
      const days = Math.floor(totalSeconds / (3600 * 24));
      const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${days.toString().padStart(2, "0")}:${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }, [currentTime]);

  const HEADER_HEIGHT = 64;

  return (
    <PageWrapper
      setCurrentPage={setCurrentPage}
      isSidebarOpen={isSidebarOpen}
      setIsSidebarOpen={setIsSidebarOpen}
      backgroundClass="bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900"
    >
      <ToastContainer />
      <SharedHeader
        setCurrentPage={setCurrentPage}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />

      <div className="flex flex-1 relative min-h-0">
        <Sidebar
          isOpen={isSidebarOpen}
          toggleSidebar={setIsSidebarOpen}
          setCurrentPage={setCurrentPage}
        />

        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        <main
          className="flex-1 flex flex-col items-center px-2 py-2 overflow-auto"
          style={{ paddingTop: HEADER_HEIGHT + 5 }}
        >
          {/* Title */}
          <div className="text-center mb-2">
            <h1 className="text-lg font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              {t.title}
            </h1>
          </div>

          {/* Table */}
          {loading && (
            <div className="flex flex-col items-center py-6">
              <div className="w-6 h-6 border-3 border-purple-500/30 border-t-pink-500 rounded-full animate-spin" />
              <p className="text-gray-400 text-xs mt-2">{t.loading}</p>
            </div>
          )}

          {!loading && games.length === 0 && (
            <div className="text-center py-6">
              <div className="text-3xl mb-1">🎮</div>
              <p className="text-gray-400 text-xs">{t.noGames}</p>
            </div>
          )}

          {!loading && games.length > 0 && (
            <div className="w-full max-w-md">
              <div className="bg-black/40 backdrop-blur-md rounded-xl border border-white/10 overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-5 gap-1 px-2 py-2 bg-purple-800/30 border-b border-white/10">
                  <div className="text-purple-300 font-semibold text-[10px] uppercase tracking-wide">{t.stake}</div>
                  <div className="text-purple-300 font-semibold text-[10px] uppercase tracking-wide">{t.status}</div>
                  <div className="text-purple-300 font-semibold text-[10px] uppercase tracking-wide text-center">{t.players}</div>
                  <div className="text-purple-300 font-semibold text-[10px] uppercase tracking-wide text-right">{t.prize}</div>
                  <div className="text-purple-300 font-semibold text-[10px] uppercase tracking-wide text-center">{t.action}</div>
                </div>

                {/* Table Rows */}
                <AnimatePresence mode="popLayout">
                  {sortedGames.map((gameData) => {
                    const stakeAmount = parseInt(String(gameData.stake_amount).replace(/[^\d]/g, ""), 10) || 0;
                    const playersCount = Number(gameData.players_count || 0);
                    const cartellasCount = Number(gameData.cartellas_count || playersCount || 1);
                    const totalPool = stakeAmount * cartellasCount;
                    const potentialWin = Math.floor(totalPool * 0.75);
                    const timeToStart = gameData.next_draw_time ? getTimeRemaining(gameData.next_draw_time, gameData.is_weekly_paying) : null;
                    const statusDisplay = getStatusDisplay(gameData, timeToStart, t);
                    const buttonDisplay = getButtonDisplay(gameData, timeToStart, t);
                    const isDisabled = buttonDisplay.disabled;

                    return (
                      <motion.div
                        key={gameData.id || `stake-${gameData.stake_id}`}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`grid grid-cols-5 gap-1 px-2 py-2 border-b border-white/5 transition-all duration-200 ${
                          !isDisabled ? "hover:bg-white/5 cursor-pointer" : "opacity-60"
                        }`}
                        onClick={() => !isDisabled && handleAction(gameData)}
                      >
                        {/* Stake Amount */}
                        <div className="flex items-center">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
                            <span className="text-white font-bold text-xs">{stakeAmount}</span>
                          </div>
                        </div>

                        {/* Status */}
                        <div className="flex items-center">
                          <span className={`text-[10px] font-semibold ${statusDisplay.color}`}>
                            {statusDisplay.text}
                          </span>
                        </div>

                        {/* Players Count */}
                        <div className="flex items-center justify-center">
                          <span className="text-gray-300 text-[10px] font-medium">{playersCount}</span>
                        </div>

                        {/* Prize */}
                        <div className="flex items-center justify-end">
                          <span className="text-green-400 font-semibold text-[10px]">
                            {potentialWin.toLocaleString()}
                          </span>
                        </div>

                        {/* Action Button */}
                        <div className="flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDisabled) handleAction(gameData);
                            }}
                            disabled={isDisabled}
                            className={`px-2 py-1 rounded-md text-[9px] font-bold transition-all duration-200 ${getButtonStyle(buttonDisplay.variant)}`}
                          >
                            {buttonDisplay.text}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          )}
        </main>
      </div>
    </PageWrapper>
  );
};

export default StakeSelectionPage;